import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ApiError, api } from './api.js';

export interface Me {
  id: string;
  wallet_address: string;
  deposit_address: string;
  created_at: number;
}

interface AuthContextValue {
  me: Me | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  signInWithSiwe: (input: {
    address: `0x${string}`;
    sign: (message: string) => Promise<`0x${string}`>;
  }) => Promise<Me>;
}

const Ctx = createContext<AuthContextValue | null>(null);

const buildSiweMessage = (params: {
  domain: string;
  address: string;
  uri: string;
  chainId: number;
  nonce: string;
  issuedAt: string;
}): string =>
  `${params.domain} wants you to sign in with your Ethereum account:\n${params.address}\n\nLog in.\n\nURI: ${params.uri}\nVersion: 1\nChain ID: ${params.chainId}\nNonce: ${params.nonce}\nIssued At: ${params.issuedAt}`;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const m = await api.get<Me>('/me');
      setMe(m);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setMe(null);
      } else {
        throw e;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => setLoading(false));
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setMe(null);
    }
  }, []);

  const signInWithSiwe = useCallback(
    async (input: {
      address: `0x${string}`;
      sign: (message: string) => Promise<`0x${string}`>;
    }) => {
      const { nonce } = await api.post<{ nonce: string }>('/auth/siwe/nonce', {
        address: input.address,
      });
      const message = buildSiweMessage({
        domain: window.location.host,
        address: input.address,
        uri: window.location.origin,
        chainId: 1,
        nonce,
        issuedAt: new Date().toISOString(),
      });
      const signature = await input.sign(message);
      const { user } = await api.post<{ ok: boolean; user: Me }>('/auth/siwe/verify', {
        message,
        signature,
      });
      setMe(user);
      return user;
    },
    [],
  );

  return (
    <Ctx.Provider value={{ me, loading, refresh, signOut, signInWithSiwe }}>
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
};
